import {
  DOCUMENT_ASSEMBLER_KEY_FORMAT_REGEX,
  DocumentAssemblerGenerationStatus,
  DocumentAssemblerGenerationValueDisposition,
  DocumentAssemblerTemplateVersionStatus,
  IDocumentAssemblerGenerationPlaceholder,
} from '@commenda/api';
import { slugify } from '@commenda/utils';

import type {
  DocumentAssemblerBadgeMeta,
  DocumentAssemblerGenerationTally,
  DocumentAssemblerVersionLifecycle,
  DocumentAssemblerVersionLifecycleInput,
} from './document-assembler.types';

export class DocumentAssemblerHelper {
  private static readonly VERSION_STATUS_BADGES: Record<
    DocumentAssemblerTemplateVersionStatus,
    DocumentAssemblerBadgeMeta
  > = {
    [DocumentAssemblerTemplateVersionStatus.DRAFT]: {
      variant: 'gray',
      label: 'Draft',
    },
    [DocumentAssemblerTemplateVersionStatus.PUBLISHED]: {
      variant: 'blue',
      label: 'Published',
    },
    [DocumentAssemblerTemplateVersionStatus.ARCHIVED]: {
      variant: 'gray',
      label: 'Archived',
    },
  };

  private static readonly GENERATION_STATUS_BADGES: Record<
    DocumentAssemblerGenerationStatus,
    DocumentAssemblerBadgeMeta
  > = {
    [DocumentAssemblerGenerationStatus.PENDING]: {
      variant: 'warning',
      label: 'In progress',
    },
    [DocumentAssemblerGenerationStatus.SUCCESS]: {
      variant: 'success',
      label: 'Produced',
    },
    [DocumentAssemblerGenerationStatus.FAILED]: {
      variant: 'error',
      label: 'Failed',
    },
  };

  /**
   * Only appended where a new version can still be uploaded, so it is deliberately absent from the
   * archived-template notice: an archived template takes no new versions.
   */
  private static readonly NEW_VERSION_CONVENTIONS_NOTICE =
    'To change them, upload a new version of the template. A new version starts with a copy of these conventions, which you can edit until you publish it.';

  static getVersionStatusBadge(
    status: DocumentAssemblerTemplateVersionStatus
  ): DocumentAssemblerBadgeMeta {
    return (
      DocumentAssemblerHelper.VERSION_STATUS_BADGES[status] ?? {
        variant: 'gray',
        label: status,
      }
    );
  }

  static describeCompany(
    companyId: number,
    companyName: string | null
  ): string {
    return companyName ?? `Company #${companyId}`;
  }

  static describeCorporation(
    corporationId: number | null,
    corporationName: string | null
  ): string | null {
    if (corporationId === null) return null;

    return corporationName ?? `Entity #${corporationId}`;
  }

  static getGenerationStatusBadge(
    status: DocumentAssemblerGenerationStatus
  ): DocumentAssemblerBadgeMeta {
    return (
      DocumentAssemblerHelper.GENERATION_STATUS_BADGES[status] ?? {
        variant: 'gray',
        label: status,
      }
    );
  }

  static getPlaceholderAnswerBadge(
    placeholder: IDocumentAssemblerGenerationPlaceholder
  ): DocumentAssemblerBadgeMeta {
    if (
      placeholder.disposition ===
      DocumentAssemblerGenerationValueDisposition.UNDECIDED
    ) {
      return { variant: 'warning', label: 'Branch not decided' };
    }

    if (
      placeholder.disposition ===
      DocumentAssemblerGenerationValueDisposition.REMOVED_BY_BRANCH_DECISION
    ) {
      return { variant: 'gray', label: 'Removed by a branch decision' };
    }

    // Not "still needed": nothing is waiting on it. The document renders with the instruction marked in the
    // page for a person to complete, so the gap is visible rather than blocking.
    if (placeholder.value === null) {
      return { variant: 'warning', label: 'Left pending' };
    }

    return { variant: 'success', label: 'In the document' };
  }

  /**
   * The three disposition predicates live beside `getPlaceholderAnswerBadge` on purpose: the badge and
   * everything that styles or counts a slot must read the same three states off `disposition`. Testing
   * `value` alone cannot tell an undecided branch apart from a slot nothing answered.
   */
  static isLeftOut(
    placeholder: IDocumentAssemblerGenerationPlaceholder
  ): boolean {
    return (
      placeholder.disposition ===
      DocumentAssemblerGenerationValueDisposition.REMOVED_BY_BRANCH_DECISION
    );
  }

  static isUndecided(
    placeholder: IDocumentAssemblerGenerationPlaceholder
  ): boolean {
    return (
      placeholder.disposition ===
      DocumentAssemblerGenerationValueDisposition.UNDECIDED
    );
  }

  /** Only a slot that IS in the document can be pending: a person has to complete it by hand. */
  static isPending(
    placeholder: IDocumentAssemblerGenerationPlaceholder
  ): boolean {
    return (
      placeholder.disposition ===
        DocumentAssemblerGenerationValueDisposition.IN_DOCUMENT &&
      placeholder.value === null
    );
  }

  static tally(
    placeholders: IDocumentAssemblerGenerationPlaceholder[]
  ): DocumentAssemblerGenerationTally {
    return {
      written: placeholders.filter(
        (placeholder) =>
          placeholder.disposition ===
            DocumentAssemblerGenerationValueDisposition.IN_DOCUMENT &&
          placeholder.value !== null
      ).length,
      pending: placeholders.filter((placeholder) => this.isPending(placeholder))
        .length,
      undecided: placeholders.filter((placeholder) =>
        this.isUndecided(placeholder)
      ).length,
      leftOut: placeholders.filter((placeholder) => this.isLeftOut(placeholder))
        .length,
    };
  }

  static suggestKeyFromName(name: string): string {
    return slugify(name);
  }

  static isValidKey(key: string): boolean {
    return DOCUMENT_ASSEMBLER_KEY_FORMAT_REGEX.test(key);
  }

  /** File.fileName is not on the wire for either the source or the output file. */
  static buildVersionFileName(templateKey: string, version: number): string {
    return `${templateKey}-v${version}.docx`;
  }

  static buildVersionOriginalDocumentFileName(
    templateKey: string,
    version: number
  ): string {
    return `${templateKey}-v${version}-original.docx`;
  }

  static buildGenerationFileName(
    templateKey: string,
    version: number,
    generationId: string
  ): string {
    return `${templateKey}-v${version}-${generationId}.docx`;
  }

  static isForbidden(error: unknown): boolean {
    return (
      typeof error === 'object' &&
      error !== null &&
      'status' in error &&
      (error as { status?: number }).status === 403
    );
  }

  static getVersionLifecycle({
    version,
    template,
  }: DocumentAssemblerVersionLifecycleInput): DocumentAssemblerVersionLifecycle {
    const isTemplateArchived = Boolean(template?.archivedAt);
    const isDraft =
      version.status === DocumentAssemblerTemplateVersionStatus.DRAFT;
    const isPublished =
      version.status === DocumentAssemblerTemplateVersionStatus.PUBLISHED;
    const isArchived =
      version.status === DocumentAssemblerTemplateVersionStatus.ARCHIVED;

    if (isTemplateArchived) {
      return {
        stateSentence:
          'The template is archived, so its versions can no longer be changed.',
        isEditable: false,
        conventionsNotice:
          'The template is archived, so the conventions on its versions can no longer be changed.',
        canPublish: false,
        publishBlockedReason: null,
        canActivate: false,
        canArchive: false,
        archiveBlockedReason: null,
      };
    }

    if (isArchived) {
      return {
        stateSentence:
          'Archived. It is kept as a record of what it produced, and nothing further happens to it.',
        isEditable: false,
        conventionsNotice: `This version is archived, so its conventions are frozen. ${DocumentAssemblerHelper.NEW_VERSION_CONVENTIONS_NOTICE}`,
        canPublish: false,
        publishBlockedReason: null,
        canActivate: false,
        canArchive: false,
        archiveBlockedReason: null,
      };
    }

    if (isDraft) {
      return {
        stateSentence: version.hasPassedValidation
          ? 'Draft. This file can be used — publish it, then mark it active.'
          : 'Draft. This file cannot be used until the problems in it are fixed.',
        isEditable: true,
        conventionsNotice:
          'You can edit these conventions while this version is a draft. Publishing freezes them, and after that the only way to change them is a new version.',
        // canBePublished is the server's own answer; never recomputed here.
        canPublish: version.canBePublished,
        publishBlockedReason: version.canBePublished
          ? null
          : version.errorCount > 0
            ? `Fix the ${version.errorCount} problem(s) in this file first.`
            : 'This file has no readable check result. Replace it to have it read again.',
        canActivate: false,
        canArchive: true,
        archiveBlockedReason: null,
      };
    }

    // PUBLISHED.
    return {
      stateSentence: version.isActiveVersion
        ? 'Published, and currently the active version. New documents are produced from this one.'
        : 'Published. Not the active version.',
      isEditable: false,
      conventionsNotice: `This version is published, so its conventions are frozen. ${DocumentAssemblerHelper.NEW_VERSION_CONVENTIONS_NOTICE}`,
      canPublish: false,
      publishBlockedReason: null,
      canActivate: isPublished && !version.isActiveVersion,
      canArchive: !version.isActiveVersion,
      archiveBlockedReason: version.isActiveVersion
        ? 'The active version cannot be archived. Mark another version active first.'
        : null,
    };
  }
}
